import React from "react";
import { useDispatch } from "react-redux";
import axios from "axios";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";

// jaza
import { List, TextField, Datagrid, CreateButton, EditButton, crudDelete, showNotification } from "jazasoft";

import ConfirmButton from "../../../components/ConfirmButton";
import HistoryButton from "../../../components/LibraryHistoryButton";

// helper
import { authServerUrl } from "../../../App";
import useQuery from "../../../hooks/useQuery";
import useMutation from "../../../hooks/useMutation";
import { useEntityPrivilege, useSetting } from "../../../hooks";
import SettingKeys from "../../../utils/SettingKeys";
import { WarehousFields } from "../../../utils/entityFields";
import { Role } from "../../../utils/types";

const useStyles = makeStyles(theme => ({
  buttonEdit: {
    width: theme.spacing(14)
  },
  buttonDelete: {
    width: theme.spacing(16)
  },
  buttonHistory: {
    width: theme.spacing(1)
  }
}));

const DeleteButton = ({ record, onDelete }) => {
  return (
    <ConfirmButton
      color="primary"
      variant="text"
      confirmTitle="Delete Warehouse?"
      disabled={record?.isReferenced}
      onConfirm={() => onDelete(record)}
    />
  );
};

const hasPrivilege = (roles) => {
  return roles?.includes(Role.MASTER) || roles?.includes(Role.SUPER_USER)
}

const Warehouse = props => {
  const classes = useStyles();
  const { resource, basePath, i18nKey, roles } = props;

  const { data: apis = {} } = useQuery({ url: "apis", params: { appId: "iam" } });
  const api = apis[0] || {};

  const dispatch = useDispatch();
  const [fetch] = useMutation();
  const { disableCreate, disableEdit, disableDelete } = useEntityPrivilege("warehouse");

  const onDelete = async record => {
    fetch(
      { url: `${resource}/${record?.id}`, method: "get" },
      {
        onSuccess: async warehouse => {
          if (warehouse.isReferenced) {
            dispatch(showNotification("Warehouse cannot be deleted once refernced.", { type: "danger" }));
            return;
          }
          // Delete in IAM first, then in app
          let options = {
            url: `${authServerUrl}/permissions`,
            method: "delete",
            data: { key: "warehouseId", name: record.name, value: record.id },
            headers: { "x-api-key": api?.apiKey }
          };
          try {
            const response = await axios.request(options); // Delete in IAM
            if (response.status === 204 || response.status === 200) {
              dispatch(crudDelete(resource, record.id, record, basePath, i18nKey)); // Delete in OptaCut
            }
          } catch (e) {
            console.log(e);
          }
        }
      }
    );
  };

  const fields = useSetting(SettingKeys.ENTITY_FIELDS, "warehouses");

  return (
    <List
      actions={({ basePath, roles }) => (
        <div>
          {hasPrivilege(roles) && !disableCreate && (
            <CreateButton basePath={basePath} showLabel={false} />
          )}
        </div>
      )}
      {...props}>
      <Datagrid className={classes.datagrid}>
        {fields.includes(WarehousFields.NAME) && <TextField source="name" />}
        {fields.includes(WarehousFields.CODE) && <TextField source="code" />}
        <TextField label="Unit" source="unit.name" />
        {fields.includes(WarehousFields.DESC) && <TextField source="desc" />}
        <HistoryButton cellClassName={classes.buttonHistory} i18nKey={props.i18nKey} />
        {hasPrivilege(roles) && !disableEdit && <EditButton cellClassName={classes.buttonEdit} />}
        {hasPrivilege(roles) && !disableDelete && (
          <DeleteButton cellClassName={classes.buttonEdit} onDelete={onDelete} />
        )}
      </Datagrid>
    </List>
  );
};

export default Warehouse;
